/**
 * Session store adapters — one store contract over the two durable session
 * kinds (Kimi/Cowork-style split):
 *
 *  - `chat`  → chat_sessions / chat_messages   (lightweight copilot chats)
 *  - `work`  → cortex_sessions / cortex_messages (durable work sessions)
 *
 * The shared engine calls these instead of touching tables directly, so the
 * agent window can host either kind without a schema merge.
 */

import { supabase } from "@/integrations/supabase/client";
import type { UIMessage } from "@flowstate/ai/react";
import {
  createCortexSession,
  deleteCortexSession,
  getCortexSessionMessages,
  listCortexSessions,
  saveCortexMessage,
  updateCortexSession,
} from "@/server/cortex-sessions";
import { logger } from "@/lib/logger";

export type SessionKind = "chat" | "work";

export interface AgentSessionSummary {
  id: string;
  kind: SessionKind;
  title: string;
  createdAt: string;
  lastActiveAt: string;
  projectId?: string | null;
  organizationId?: string | null;
  modelTier?: string;
  isArchived?: boolean;
}

export interface StoreMessage {
  id?: string;
  role: "user" | "assistant";
  content: string;
}

export interface SessionStore {
  kind: SessionKind;
  list(userId: string, orgId?: string): Promise<AgentSessionSummary[]>;
  ensure(
    userId: string,
    opts: { titleHint?: string; orgId?: string; projectId?: string },
  ): Promise<string | undefined>;
  loadMessages(sessionId: string): Promise<UIMessage[]>;
  saveMessage(
    sessionId: string,
    msg: StoreMessage,
    ctx: { userId: string; orgId?: string },
  ): Promise<string | undefined>;
  remove(sessionId: string): Promise<void>;
  rename(sessionId: string, title: string): Promise<void>;
}

// ── chat_sessions (drawer/copilot store) ─────────────────────────────────────

function makeChatStore(): SessionStore {
  return {
    kind: "chat",

    async list(userId, orgId) {
      // project_id is new (migration 20261014000200) — untyped until regen.
      let q = (supabase as any)
        .from("chat_sessions")
        .select("id, title, created_at, updated_at, organization_id, project_id")
        .eq("user_id", userId);
      if (orgId) q = q.eq("organization_id", orgId);
      const { data, error } = await q.order("updated_at", { ascending: false }).limit(100);
      if (error) {
        logger.warn("[SessionStore:chat] list failed", { error: error.message });
        return [];
      }
      return (data ?? []).map((s: Record<string, unknown>) => ({
        id: s.id as string,
        kind: "chat" as const,
        title: (s.title as string) ?? "Chat",
        createdAt: s.created_at as string,
        lastActiveAt: (s.updated_at as string) ?? (s.created_at as string),
        projectId: (s.project_id as string | null) ?? null,
        organizationId: (s.organization_id as string | null) ?? null,
      }));
    },

    async ensure(userId, opts) {
      const { data, error } = await (supabase as any)
        .from("chat_sessions")
        .insert({
          user_id: userId,
          title: opts.titleHint?.slice(0, 80) || "New conversation",
          ...(opts.orgId ? { organization_id: opts.orgId } : {}),
          ...(opts.projectId ? { project_id: opts.projectId } : {}),
        })
        .select("id")
        .single();
      if (error) {
        logger.warn("[SessionStore:chat] ensure failed", { error: error.message });
        return undefined;
      }
      return data.id as string;
    },

    async loadMessages(sessionId) {
      const { data, error } = await supabase
        .from("chat_messages")
        .select("id, role, content, created_at")
        .eq("conversation_id", sessionId)
        .order("created_at", { ascending: true })
        .limit(200);
      if (error) {
        logger.warn("[SessionStore:chat] loadMessages failed", { error: error.message });
        return [];
      }
      return (data ?? []).map((m) => ({
        id: m.id as string,
        role: m.role as "user" | "assistant",
        parts: [{ type: "text" as const, content: m.content as string }],
      }));
    },

    async saveMessage(sessionId, msg) {
      const { data, error } = await supabase
        .from("chat_messages")
        .insert({
          conversation_id: sessionId,
          role: msg.role,
          content: msg.content,
        })
        .select("id")
        .single();
      if (error) {
        logger.warn("[SessionStore:chat] saveMessage failed", { error: error.message });
        return undefined;
      }
      return data?.id as string | undefined;
    },

    async remove(sessionId) {
      await supabase
        .from("chat_sessions")
        .delete()
        .eq("id", sessionId)
        .eq("user_id", (await supabase.auth.getUser()).data.user?.id ?? "");
    },

    async rename(sessionId, title) {
      await supabase.from("chat_sessions").update({ title }).eq("id", sessionId);
    },
  };
}

// ── cortex_sessions (durable work sessions) ──────────────────────────────────

function makeWorkStore(getToken: () => Promise<string | undefined>): SessionStore {
  return {
    kind: "work",

    async list(_userId, orgId) {
      const token = await getToken();
      if (!token) return [];
      const { sessions } = await listCortexSessions({ data: { accessToken: token } });
      return (sessions ?? [])
        .filter((s) => !orgId || true) // org scoping handled server-side later
        .map((s) => ({
          id: s.id,
          kind: "work" as const,
          title: s.title,
          createdAt: s.started_at,
          lastActiveAt: s.last_active_at,
          modelTier: s.model_tier,
          isArchived: s.is_archived,
          projectId: s.project_id ?? null,
        }));
    },

    async ensure(_userId, opts) {
      const token = await getToken();
      if (!token) return undefined;
      const { session } = await createCortexSession({
        data: {
          accessToken: token,
          title: opts.titleHint?.slice(0, 80),
          initialMessage: opts.titleHint,
          projectId: opts.projectId,
        },
      });
      return session?.id;
    },

    async loadMessages(sessionId) {
      const token = await getToken();
      if (!token) return [];
      const { messages } = await getCortexSessionMessages({
        data: { accessToken: token, sessionId },
      });
      return (messages ?? []).map((m) => ({
        id: m.id,
        role: m.role,
        parts: [{ type: "text" as const, content: m.content }],
      }));
    },

    async saveMessage(sessionId, msg) {
      const token = await getToken();
      if (!token) return undefined;
      await saveCortexMessage({
        data: {
          accessToken: token,
          sessionId,
          role: msg.role,
          content: msg.content,
        },
      });
      return undefined;
    },

    async remove(sessionId) {
      const token = await getToken();
      if (!token) return;
      await deleteCortexSession({ data: { accessToken: token, sessionId } });
    },

    async rename(sessionId, title) {
      const token = await getToken();
      if (!token) return;
      await updateCortexSession({
        data: { accessToken: token, sessionId, title },
      });
    },
  };
}

export function createSessionStore(
  kind: SessionKind,
  getToken: () => Promise<string | undefined>,
): SessionStore {
  return kind === "chat" ? makeChatStore() : makeWorkStore(getToken);
}
